import { css } from '@emotion/react'
import api from 'api'
import { Button, VFlow } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { FormFooter } from 'components/form'
import { PageLoading } from 'components/loading'
import { RelatoriosButtonGroup } from 'components/relatorios-buttons/RelatoriosButtonGroup'
import { usePagedTableProps } from 'components/table'
import { ArquivoTemporarioStatusEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { readFileError } from 'hooks/impressao/util-impressao'
import { Fragment, SetStateAction } from 'react'
import { FormRenderProps, useFormState } from 'react-final-form'
import Permissions from 'types/Permissions'
import { isUndefinedOrNull } from 'util/checks'
import { LinkVerEquipesVinculadasView } from 'view/vinculacao-equipes/LinkVerEquipesVinculadasView'

import { convertAcompanhamentoCondicaoSaudeModelToQueryInput } from '../../converter-acompanhamentoCondicaoSaude'
import { DoSearchPanel } from '../../DoSearchPanel'
import { BuscaCidadaoQueryResult } from '../../model/lista-tematica/model-acompanhamentoCondicaoSaudeListaTematica'
import { AcompanhamentoCondicaoSaudeFormModel } from '../../model/model-acompanhamentoCondicaoSaude'
import { AcompanhamentoCondicaoSaudeActiveFilters } from '../filter/AcompanhamentoCondicaoSaudeActiveFilters'
import { AcompanhamentoCondicaoSaudeTable } from './AcompanhamentoCondicaoSaudeTable'
import { AcompanhamentoCondicaoSaudeTableHeader } from './AcompanhamentoCondicaoSaudeTableHeader'

interface AcompanhamentoCondicaoSaudeTablePanelProps extends FormRenderProps<AcompanhamentoCondicaoSaudeFormModel> {
  currentFilter?: AcompanhamentoCondicaoSaudeFormModel
  statusRelatorio: ArquivoTemporarioStatusEnum
  data?: BuscaCidadaoQueryResult
  loading: boolean
  onChangeTableProps: (filter: SetStateAction<AcompanhamentoCondicaoSaudeFormModel>) => void
  setShouldRefetch: (shouldRefetch: boolean) => void
  shouldRefetch: boolean
  abortQuery: () => void
}

export function AcompanhamentoCondicaoSaudeTablePanel(props: AcompanhamentoCondicaoSaudeTablePanelProps) {
  const {
    currentFilter,
    statusRelatorio,
    data,
    loading,
    onChangeTableProps,
    setShouldRefetch,
    shouldRefetch,
    values,
    abortQuery,
  } = props

  const { analytics } = useFirebase()

  const { hasAuthorization } = useSession()

  const hasTriaFilter = !isUndefinedOrNull(currentFilter?.tria)
  const hasIvcfFilter = !isUndefinedOrNull(currentFilter?.filtrosAvancados?.ivcf)

  const isCoordenador = hasAuthorization(Permissions.acompanhamentos.condicoesDeSaude.todasAsEquipes)

  const tableProps = usePagedTableProps({
    result: data,
    loading,
    onChange: onChangeTableProps,
  })

  const styles = createStyles()

  const afterSubmit = () => {
    setShouldRefetch(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleExportarCSVClick = () => {
    analytics.logEvent('exportar_csv_acompanhamento_condicoes_saude')
    api.acompanhamentoSaude
      .exportarCSV(convertAcompanhamentoCondicaoSaudeModelToQueryInput(currentFilter))
      .catch(readFileError)
    afterSubmit()
  }

  const handleExportarPDFClick = () => {
    analytics.logEvent('exportar_pdf_acompanhamento_condicoes_sa')
    api.acompanhamentoSaude
      .exportarPDF(convertAcompanhamentoCondicaoSaudeModelToQueryInput(currentFilter))
      .catch(readFileError)
    afterSubmit()
  }

  const { dirtySinceLastSubmit } = useFormState({
    subscription: { dirtySinceLastSubmit: true },
  })
  const hasData = !isUndefinedOrNull(data)
  const hideTable = (!hasData && !loading) || (hasData && dirtySinceLastSubmit)

  const { equipeResponsavel } = values ?? {}

  return hideTable ? (
    <DoSearchPanel isCoordenador={isCoordenador} equipeResponsavelId={equipeResponsavel?.id} />
  ) : (
    <Fragment>
      <AcompanhamentoCondicaoSaudeTableHeader hasTriaFilter={hasTriaFilter} />
      <VFlow>
        {currentFilter && <AcompanhamentoCondicaoSaudeActiveFilters filtro={currentFilter} />}
        <LinkVerEquipesVinculadasView equipe={currentFilter?.equipeResponsavel} />
        {loading ? (
          <Fragment>
            <PageLoading message='Em processamento. É possível cancelar e refazer a busca.' />
            <VFlow style={styles.cancelButton}>
              <Button skin='default' size='small' kind='normal' onClick={() => abortQuery()}>
                Cancelar busca
              </Button>
            </VFlow>
          </Fragment>
        ) : (
          <AcompanhamentoCondicaoSaudeTable {...tableProps} hasIvcfFilter={hasIvcfFilter} />
        )}
      </VFlow>
      <FormFooter>
        <RelatoriosButtonGroup
          hasIvcfFilter={hasIvcfFilter}
          shouldRefetch={shouldRefetch}
          isAcompanhamentoCondicoesSaude
          statusRelatorio={statusRelatorio}
          numberOfElements={data?.content.length}
          onClickExportarCSV={handleExportarCSVClick}
          onClickExportarPDF={handleExportarPDFClick}
        />
      </FormFooter>
    </Fragment>
  )
}

const createStyles = () => ({
  cancelButton: css`
    text-align: center;
    margin-top: 0.75rem;
  `,
})
